import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { getOwnerEmail, setOwnerEmail } from "../lib/store";
import { Spinner } from "./ui";

export function InsightsPage() {
  const [email, setEmail] = useState(getOwnerEmail());
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function run(e: string) {
    if (!e) return;
    setLoading(true);
    setErr("");
    try {
      setOwnerEmail(e);
      const { insights } = await api.insights(e);
      setData(insights);
    } catch (er: any) {
      setErr(er.message || "Could not load insights");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (email) run(email); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="card">
        <h1 className="text-2xl font-extrabold">Your travel insights</h1>
        <p className="mt-1 text-sm text-slate-500">Patterns across every trip you've saved with the same email — your evolving traveler profile.</p>
        <div className="mt-4 flex gap-2">
          <input className="input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run(email)} />
          <button className="btn-primary" onClick={() => run(email)}>Load</button>
        </div>
      </div>

      {loading && <div className="card"><Spinner label="Crunching your history…" /></div>}
      {err && <div className="card text-red-600">{err}</div>}

      {data && data.empty && (
        <div className="card text-center text-slate-500">No saved trips for {email} yet. Create a trip with this email to start building insights.</div>
      )}

      {data && !data.empty && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat label="Trips planned" value={data.tripCount} />
            <Stat label="Travelers hosted" value={data.travelerCountTotal} />
            <Stat label="Favourite pace" value={data.favouritePace || "—"} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <RankCard title="What you travel for" items={data.topObjectives} />
            <RankCard title="Recurring interests" items={data.topInterests} />
          </div>

          {Object.keys(data.avgBudgetByCurrency || {}).length > 0 && (
            <div className="card">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Average trip budget</h3>
              <div className="flex flex-wrap gap-4">
                {Object.entries(data.avgBudgetByCurrency).map(([c, v]) => (
                  <div key={c} className="text-lg font-bold">{c} {v as number}</div>
                ))}
              </div>
            </div>
          )}

          <div className="card">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">Trip timeline</h3>
            <ol className="relative space-y-3 border-l-2 border-slate-100 pl-4">
              {data.timeline.map((t: any, i: number) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[1.35rem] top-1 h-3 w-3 rounded-full bg-brand-500 ring-4 ring-white" />
                  <p className="font-semibold">{t.title}</p>
                  <p className="text-xs text-slate-500">
                    {t.destination}{t.date ? ` · ${t.date}` : ""}{t.estTotal ? ` · ~${t.currency} ${Math.round(t.estTotal)}` : ""}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="card">
      <p className="text-3xl font-extrabold text-brand-600">{value}</p>
      <p className="mt-1 text-sm text-slate-500">{label}</p>
    </div>
  );
}

function RankCard({ title, items }: { title: string; items: { label: string; count: number }[] }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="card">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-400">{title}</h3>
      {items.length === 0 && <p className="text-sm text-slate-400">Not enough data yet.</p>}
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.label}>
            <div className="flex justify-between text-sm"><span className="capitalize">{i.label}</span><span className="text-slate-400">{i.count}</span></div>
            <div className="mt-0.5 h-1.5 rounded-full bg-slate-100">
              <div className="h-1.5 rounded-full bg-brand-500" style={{ width: `${(i.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
